import React from "react";
import {
  Box,
  Typography,
  Card,
  CardContent,
  CircularProgress,
  useTheme,
} from "@mui/material";
import { Psychology as AiIcon } from "@mui/icons-material";

interface GeneratingViewProps {
  ticker: string;
}

/**
 * View displayed when a stock analysis report is being generated.
 * Shows a simple generating animation with helpful messaging.
 */
const GeneratingView: React.FC<GeneratingViewProps> = ({ ticker }) => {
  const theme = useTheme();

  return (
    <Box sx={{ px: { xs: 2, md: 3 }, pb: { xs: 2, md: 3 } }}>
      <Card
        sx={{
          width: "100%",
          borderRadius: 3,
          backgroundColor:
            theme.palette.mode === "dark"
              ? "rgba(255, 255, 255, 0.02)"
              : "rgba(0, 0, 0, 0.02)",
          border:
            theme.palette.mode === "dark"
              ? "1px solid rgba(255, 255, 255, 0.1)"
              : "1px solid rgba(0, 0, 0, 0.1)",
        }}
      >
        <CardContent sx={{ px: 6, py: 10, textAlign: "center" }}>
          {/* Animated generating icon */}
          <Box
            sx={{
              position: "relative",
              display: "inline-flex",
              mb: 4,
            }}
          >
            <CircularProgress
              size={80}
              thickness={2}
              sx={{
                color: "#22c55e",
                position: "absolute",
                top: -8,
                left: -8,
              }}
            />
            <AiIcon
              sx={{
                fontSize: 64,
                color: "#16a34a",
              }}
            />
          </Box>

          <Typography
            variant="h5"
            sx={{
              fontWeight: 600,
              mb: 3,
              color: theme.palette.mode === "dark" ? "#ffffff" : "#1d1d1f",
            }}
          >
            Generating...
          </Typography>

          <Typography
            variant="body1"
            sx={{
              mb: 4,
              color: theme.palette.mode === "dark" ? "#a1a1a6" : "#86868b",
              lineHeight: 1.6,
              maxWidth: 500,
              mx: "auto",
            }}
          >
            Analysing financial data, competitive positioning, news, and market
            trends to generate your report.
          </Typography>

          <Typography
            variant="body2"
            sx={{
              color: theme.palette.mode === "dark" ? "#a1a1a6" : "#86868b",
              fontStyle: "italic",
            }}
          >
            This can take up to a minute. You can navigate away and the report
            will continue generating in the background.
          </Typography>
        </CardContent>
      </Card>
    </Box>
  );
};

export default GeneratingView;
